/**
 * FFSummaryCard — En-tête riche de la page Détail facture fournisseur.
 *
 * Objectif UX : voir d'un seul coup d'œil l'état de la facture.
 *   - Big-screen statut + référence interne
 *   - Tuiles : Fournisseur · Total TTC · Reste à payer · Échéance
 *   - Bandeau d'info contextuel (rejetée, payée, à exporter, à valider…)
 */
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Building2, Banknote, Wallet, Calendar as CalendarIcon, Loader2, Sparkles } from "lucide-react";

const STATUT_LABELS = {
  brouillon: "Brouillon", a_valider: "À valider", validee: "Validée",
  a_payer: "À payer", payee: "Payée", rejetee: "Rejetée",
  a_exporter: "À exporter", exportee: "Exportée",
};
const STATUT_STYLES = {
  brouillon: { bg: "bg-gray-100", text: "text-gray-700", ring: "ring-gray-300", icon: "📝" },
  a_valider: { bg: "bg-amber-100", text: "text-amber-800", ring: "ring-amber-300", icon: "⏳" },
  validee:   { bg: "bg-blue-100", text: "text-blue-800", ring: "ring-blue-300", icon: "✓" },
  a_payer:   { bg: "bg-orange-100", text: "text-orange-800", ring: "ring-orange-300", icon: "💳" },
  payee:     { bg: "bg-emerald-100", text: "text-emerald-800", ring: "ring-emerald-300", icon: "✅" },
  rejetee:   { bg: "bg-red-100", text: "text-red-800", ring: "ring-red-300", icon: "❌" },
  a_exporter:{ bg: "bg-indigo-100", text: "text-indigo-800", ring: "ring-indigo-300", icon: "📤" },
  exportee:  { bg: "bg-purple-100", text: "text-purple-800", ring: "ring-purple-300", icon: "📨" },
};

function fmtChf(v) {
  if (v == null || isNaN(v)) return "—";
  return Number(v).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("fr-CH"); } catch { return d; }
}
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = Math.floor((new Date(dateStr).getTime() - Date.now()) / (1000 * 3600 * 24));
  return diff;
}

export default function FFSummaryCard({ f, onBack, onOCR, ocrRunning, editable }) {
  const st = STATUT_STYLES[f.statut] || STATUT_STYLES.brouillon;
  const reste = f.reste_a_payer ?? f.montant_ttc ?? 0;
  const totalCredite = f.total_notes_credit || 0;
  const ttc = f.montant_ttc || 0;
  const echeance = f.date_echeance;
  const dleft = daysUntil(echeance);
  const isPaye = f.statut === "payee";

  // Code couleur de l'échéance
  let echeanceColor = "text-gray-600";
  let echeanceLabel = "";
  if (echeance && !isPaye) {
    if (dleft < 0) { echeanceColor = "text-red-700"; echeanceLabel = `${Math.abs(dleft)}j de retard`; }
    else if (dleft <= 7) { echeanceColor = "text-orange-600"; echeanceLabel = `dans ${dleft}j`; }
    else if (dleft <= 30) { echeanceColor = "text-amber-600"; echeanceLabel = `dans ${dleft}j`; }
    else { echeanceColor = "text-emerald-600"; echeanceLabel = `dans ${dleft}j`; }
  }

  return (
    <Card className="p-4 sm:p-5 mb-4 border-l-4 border-l-orange-400 shadow-sm" data-testid="ff-summary-card">
      {/* Ligne 1 : retour + référence + statut + actions globales */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="ff-back-btn" className="shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{f.reference_interne}</h1>
              {f.numero_facture && (
                <span className="text-xs text-gray-500">
                  N° fournisseur : <span className="font-mono">{f.numero_facture}</span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ring-1 ${st.bg} ${st.text} ${st.ring}`}
                data-testid="ff-status-badge"
              >
                <span className="text-sm leading-none">{st.icon}</span>
                {STATUT_LABELS[f.statut] || f.statut}
              </span>
              {f.notes_credit?.length > 0 && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300 text-[10px]">
                  {f.notes_credit.length} note(s) de crédit · −{fmtChf(totalCredite)} CHF
                </Badge>
              )}
              {f.bank_export_msg_id && (
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-300 text-[10px]">
                  📨 Dans lot pain.001
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {editable && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOCR}
              disabled={ocrRunning}
              data-testid="ff-ocr-btn"
            >
              {ocrRunning ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 mr-1.5 text-amber-500" />
              )}
              {ocrRunning ? "Analyse IA…" : (f.donnees_ocr ? "Réanalyser" : "Lancer l'analyse IA")}
            </Button>
          )}
        </div>
      </div>

      {/* Ligne 2 : tuiles métriques */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        {/* Fournisseur */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-testid="ff-tile-fournisseur">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1 mb-1">
            <Building2 className="w-3 h-3" /> Fournisseur
          </div>
          <div className="text-sm font-semibold text-gray-900 truncate" title={f.fournisseur_nom}>
            {f.fournisseur_nom || "—"}
          </div>
          <div className="text-[10px] text-gray-400 truncate font-mono mt-0.5">
            {f.donnees_ocr?.fournisseur_iban || f.fournisseur_iban || "Pas d'IBAN"}
          </div>
        </div>

        {/* Total TTC */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-testid="ff-tile-total">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1 mb-1">
            <Banknote className="w-3 h-3" /> Total TTC
          </div>
          <div className="text-lg sm:text-xl font-bold text-gray-900 font-mono">
            {fmtChf(ttc)} <span className="text-xs text-gray-500">{f.devise || "CHF"}</span>
          </div>
          {f.tva_montant > 0 && (
            <div className="text-[10px] text-gray-400 mt-0.5">
              HT {fmtChf(f.montant_ht)} · TVA {f.tva_taux}% : {fmtChf(f.tva_montant)}
            </div>
          )}
        </div>

        {/* Reste à payer */}
        <div className={`rounded-lg p-3 border ${isPaye ? "bg-emerald-50 border-emerald-200" : reste > 0 ? "bg-orange-50 border-orange-200" : "bg-gray-50 border-gray-200"}`} data-testid="ff-tile-reste">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1 mb-1">
            <Wallet className="w-3 h-3" /> Reste à payer
          </div>
          <div className={`text-lg sm:text-xl font-bold font-mono ${isPaye ? "text-emerald-700" : reste > 0 ? "text-orange-700" : "text-gray-900"}`}>
            {fmtChf(reste)} <span className="text-xs">{f.devise || "CHF"}</span>
          </div>
          {totalCredite > 0 && (
            <div className="text-[10px] text-purple-600 mt-0.5">Notes de crédit : −{fmtChf(totalCredite)}</div>
          )}
          {isPaye && f.paye_at && (
            <div className="text-[10px] text-emerald-700 mt-0.5">
              Payée le {fmtDate(f.paye_at)} · {f.paye_mode}
            </div>
          )}
        </div>

        {/* Échéance */}
        <div className={`rounded-lg p-3 border ${dleft < 0 && !isPaye ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"}`} data-testid="ff-tile-echeance">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1 mb-1">
            <CalendarIcon className="w-3 h-3" /> Échéance
          </div>
          <div className="text-sm font-semibold text-gray-900">
            {fmtDate(echeance)}
          </div>
          {echeanceLabel && !isPaye && (
            <div className={`text-[10px] font-semibold mt-0.5 ${echeanceColor}`}>
              {echeanceLabel}
            </div>
          )}
          {!echeance && (
            <div className="text-[10px] text-gray-400 mt-0.5 italic">Non renseignée</div>
          )}
        </div>
      </div>

      {/* Bandeaux contextuels */}
      {f.statut === "rejetee" && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          <strong>Facture rejetée</strong>
          {f.rejet_motif && <> · motif : <em>{f.rejet_motif}</em></>}
        </div>
      )}
      {f.statut === "payee" && f.paye_note && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
          📝 <em>{f.paye_note}</em>
        </div>
      )}
    </Card>
  );
}
